import { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { FieldErrors, FieldValues, Path, UseFormSetError } from 'react-hook-form';

import { captureError, getErrorMessage } from './errors';
import { convertStringToSlug, yup } from './validation';

export function handleApiFormError<Values extends FieldValues>(
  apiError: any,
  values: Values,
  setFieldError: UseFormSetError<Values>,
  fieldAliases?: Record<string, string>,
) {
  const fieldErrors = apiError?.data?.field_errors;

  if (fieldErrors) {
    for (const key of Object.keys(fieldErrors)) {
      if (key in values) {
        setFieldError((fieldAliases?.[key] || key) as Path<Values>, { message: fieldErrors[key] });
      } else {
        setFieldError('root', { message: getErrorMessage(apiError) });
        captureError(apiError, {
          context: 'Form submission error',
        });

        return;
      }
    }
  } else {
    setFieldError('root', { message: getErrorMessage(apiError) });
    captureError(apiError, {
      context: 'Form submission error',
    });
  }
}

export function getSlugHelperText(value: string, label: string) {
  const isValid = yup.string().slug().isValidSync(value);
  if (!isValid) {
    return `Your ${label} will be created as ${convertStringToSlug(value)}`;
  }

  return '';
}

export function scrollToFormFieldError(errors: FieldErrors<any>) {
  const elements = Object.keys(errors)
    .map((name) => document.querySelectorAll(`[name="${name}"]`)[0])
    .filter((el) => !!el);
  elements.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

  if (elements.length > 0) {
    const errorElement = elements[0] as HTMLInputElement;
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    errorElement.focus({ preventScroll: true });
  }
}

export function getFieldHelperText(
  errorMessage: string | undefined,
  helperText: string | ReactNode,
  splitBy?: string,
) {
  if (!errorMessage) {
    return helperText;
  }

  const parsedErrorMessage = errorMessage.endsWith('.') ? `${errorMessage} ` : `${errorMessage}. `;

  return (
    <Box component="span" whiteSpace="pre-wrap">
      {parsedErrorMessage}
      {splitBy}
      <Typography color="text.secondary" component="span" variant="caption">
        {helperText}
      </Typography>
    </Box>
  );
}
